import { useState, useCallback, useMemo } from 'react';

export interface UseFilterStateOptions<T> {
  initialFilters: T;
  onFiltersChange?: (filters: T) => void;
}

export interface UseFilterStateReturn<T> {
  filters: T;
  hasSearched: boolean;
  setHasSearched: (searched: boolean) => void;
  updateFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  updateFilters: (newFilters: Partial<T>) => void;
  resetFilters: () => void;
  clearSearch: () => void;
  isFilterActive: boolean;
}

/**
 * Custom hook for managing filter state across components
 * Provides consistent filter management patterns
 */
export const useFilterState = <T extends Record<string, any>>({
  initialFilters,
  onFiltersChange
}: UseFilterStateOptions<T>): UseFilterStateReturn<T> => {
  const [filters, setFilters] = useState<T>(initialFilters);
  const [hasSearched, setHasSearched] = useState(false);

  const updateFilter = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      onFiltersChange?.(newFilters);
      return newFilters;
    });
  }, [onFiltersChange]);

  const updateFilters = useCallback((newFilters: Partial<T>) => {
    setFilters(prev => {
      const updatedFilters = { ...prev, ...newFilters };
      onFiltersChange?.(updatedFilters);
      return updatedFilters;
    });
  }, [onFiltersChange]);

  const resetFilters = useCallback(() => {
    setFilters(initialFilters);
    setHasSearched(false);
    onFiltersChange?.(initialFilters);
  }, [initialFilters, onFiltersChange]);

  const clearSearch = useCallback(() => {
    setHasSearched(false);
  }, []);

  const isFilterActive = useMemo(() => {
    return Object.keys(filters).some(key => {
      const value = filters[key];
      if (value === null || value === undefined || value === '') return false;
      if (Array.isArray(value)) return value.length > 0;
      if (typeof value === 'object' && value !== null) {
        return Object.keys(value).some(subKey => value[subKey] !== null && value[subKey] !== undefined && value[subKey] !== '');
      }
      return true;
    });
  }, [filters]);

  return {
    filters,
    hasSearched,
    setHasSearched,
    updateFilter,
    updateFilters,
    resetFilters,
    clearSearch,
    isFilterActive
  };
};
